import React, { useState, useCallback } from "react";
import "./AccountNavbar.css";
import Cropper, { Area } from "react-easy-crop";
import { Modal } from "react-bootstrap";
import getCroppedImg from "../../utils/getCroppedImg";
import axiosInstance from "../../api/axiosInstance";
import { useSelector, useDispatch } from "react-redux";
import type { RootState } from "../../store/store";
import { setUser } from "../../store/slices/userSlice";
import { useLocation, useNavigate } from "react-router";
import { logoutFun } from "../../config/auth";

type SupportInfo = {
  id: number;
  call_numbers: string; // Comma-separated phone numbers (e.g. "+971 42 97 9997,+971 51 234 5678")
  whatsapp_numbers: string; // Comma-separated WhatsApp numbers
  email: string;
  status: boolean;
};

const AccountNavbar = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const user = useSelector((state: RootState) => state.user);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [crop, setCrop] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
  const [zoom, setZoom] = useState<number>(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const [showSupportModal, setShowSupportModal] = useState(false);

  const [supportData, setSupportData] = useState<SupportInfo>();
  const handleCloseSupportModal = () => setShowSupportModal(false);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && (file.type === "image/jpeg" || file.type === "image/png")) {
      if (file.size > 5 * 1024 * 1024) {
        alert("File is too large. Maximum size is 5MB.");
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        setSelectedImage(reader.result as string);
        setIsModalOpen(true);
      };
      reader.readAsDataURL(file);
    } else {
      console.error("Invalid file type. Only jpg and png are allowed.");
    }
  };

  const handleOpenSupportModal = async () => {
    const response = await axiosInstance.get(`/api/openRoute/support-info`);
    if (response) {
      setSupportData(response.data);
    }
    setShowSupportModal(true);
  };

  const onCropComplete = useCallback((_: any, area: Area) => {
    setCroppedAreaPixels(area);
  }, []);

  const handleCropDone = async () => {
    if (!selectedImage || !croppedAreaPixels) return;

    try {
      const croppedImage = await getCroppedImg(
        selectedImage,
        croppedAreaPixels
      );
      const file = new File([croppedImage], "profile.jpg", {
        type: "image/jpeg",
      });

      const token = sessionStorage.getItem("tk");
      const formData = new FormData();

      formData.append("files", file);
      try {
        const response = await axiosInstance.post(
          `/api/user/uploadProfilePic`,
          formData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        if (response) {
          dispatch(
            setUser({
              ...user,
              avatar: response.data.userProfileData.userProfilePicBase64,
            })
          );
          setShowSuccessModal(true); // show the success modal
        } else {
          console.error("Error updating profile:", response);
        }
      } catch (error) {
        console.error("Error uploading image:", error);
      }

      setIsModalOpen(false);
    } catch (error) {
      console.error("Cropping failed", error);
    }
  };

  return (
    <div className="col-md-auto col-12 p-0">
      <div
        className="sidebar-account"
        style={{ position: "sticky", top: "100px", zIndex: 1 }}
      >
        {/* Avatar block */}
        <div className="mb-4  d-flex align-items-center mx-3 mx-md-0 gap-2 mobile-style-profile bg-main p-3 rounded-3 text-white position-relative">
          <div className="position-relative">
            <img
              src={
                user.avatar || "https://randomuser.me/api/portraits/men/75.jpg"
              }
              className="avatar-preview mb-2 rounded-circle"
              alt="Profile Image"
              style={{ width: "64px", height: "64px", objectFit: "cover" }}
            />
            <div
              className="edit-icon"
              onClick={() =>
                document.getElementById("profile-file-input")?.click()
              }
            >
              <img
                src="https://goodhand.b-cdn.net/Assets/Flow/edit-icon.svg"
                alt="Edit Profile"
              />
              <input
                type="file"
                accept="image/*"
                id="profile-file-input"
                style={{ display: "none" }}
                onChange={handleFileChange}
                onClick={(e) => (e.currentTarget.value = "")}
              />
            </div>
          </div>
          <div>
            <h6 className="mb-0">{user.name}</h6>
            <small>{user.email}</small>
          </div>
        </div>

        {/* Modal for cropping */}
        <Modal
          show={isModalOpen}
          onHide={() => setIsModalOpen(false)}
          backdrop="static"
          centered
          className="profile-pic"
        >
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body className="modal-dialog-scrollable rounded-top modal-body">
            <div>
              <p className="font-16 ff-semibold text-center text-black border-bottom pb-2">
                UPLOAD PHOTO
              </p>
            </div>

            <div className="cropper-wrapper ">
              <div
                style={{ position: "relative", width: "100%", height: 310 }}
                className="rounded"
              >
                <Cropper
                  image={selectedImage || ""}
                  crop={crop}
                  zoom={zoom}
                  aspect={1}
                  onCropChange={setCrop}
                  onZoomChange={setZoom}
                  onCropComplete={onCropComplete}
                />

                <div className="zoom-controls">
                  <button
                    onClick={() => setZoom(zoom - 0.1)}
                    className="zoom-btn"
                  >
                    −
                  </button>
                  <div className="divider" />
                  <button
                    onClick={() => setZoom(zoom + 0.1)}
                    className="zoom-btn"
                  >
                    +
                  </button>
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer className="p-0 border-0 rounded-0 shadow-none  modal-foote justify-content-center">
            <div className="d-flex align-items-center justify-content-between w-100 px-3 py-2">
              <button
                onClick={handleCropDone}
                className="btn btn-primary w-100 font-14 ff-semibold py-3  bt--cart"
              >
                Proceed
              </button>
            </div>
          </Modal.Footer>
        </Modal>

        <Modal
          show={showSuccessModal}
          onHide={() => setShowSuccessModal(false)}
          centered
        >
          {" "}
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body className="rounded-top text-center">
            <img
              src="https://goodhand.b-cdn.net/Assets/Flow/image-sucsess.png" // Replace with your icon path
              alt="Success"
            />
            <h6 className="mb-3">Profile updated successfully</h6>
          </Modal.Body>
          <Modal.Footer className="p-0 border-0 rounded-0 shadow-none  modal-foote border-top rounded-bottom justify-content-center">
            <div className="d-flex align-items-center justify-content-between w-100 px-3 py-2">
              <button
                onClick={() => setShowSuccessModal(false)}
                className="btn btn-primary w-100 font-14 ff-semibold py-3  bt--cart"
              >
                OkAY, GOT IT
              </button>
            </div>
          </Modal.Footer>
        </Modal>

        {/* Sidebar content */}
        <div className="sidebar-content-wrap">
          <div className="nav-section mb-0 mb-md-3">
            <div className="p-3 layout-border rounded">
              <p className="font-14 text-muted nav-mob-heading ">My Account</p>
              <div
                className={`nav-item dash bg-2 d-flex align-items-center gap-2 py-2 ${
                  location.pathname === "accounts/Dashboard" ? "active" : ""
                }`}
                onClick={() => {
                  navigate("accounts/Dashboard");
                }}
              >
                <div>
                  <svg
                    width="17"
                    height="20"
                    viewBox="0 0 17 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 5C3 6.06087 3.42143 7.07829 4.17157 7.82843C4.92172 8.57858 5.93913 9.00001 7 9.00001C8.06087 9.00001 9.07828 8.57858 9.82843 7.82843C10.5786 7.07829 11 6.06087 11 5C11 3.93914 10.5786 2.92172 9.82843 2.17157C9.07828 1.42143 8.06087 1 7 1C5.93913 1 4.92172 1.42143 4.17157 2.17157C3.42143 2.92172 3 3.93914 3 5Z"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 19V17C1 15.9391 1.42143 14.9217 2.17157 14.1716C2.92172 13.4214 3.93913 13 5 13H9"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 17L12 19L16 15"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ff-medium font-14">Dashboard</div>
              </div>
              <div
                className={`nav-item dash bg-2 d-flex align-items-center gap-2 py-2 ${
                  location.pathname === "/accounts/profile" ? "active" : ""
                }`}
                onClick={() => {
                  navigate("/accounts/profile");
                }}
              >
                <div>
                  <svg
                    width="17"
                    height="20"
                    viewBox="0 0 17 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M3 5C3 6.06087 3.42143 7.07829 4.17157 7.82843C4.92172 8.57858 5.93913 9.00001 7 9.00001C8.06087 9.00001 9.07828 8.57858 9.82843 7.82843C10.5786 7.07829 11 6.06087 11 5C11 3.93914 10.5786 2.92172 9.82843 2.17157C9.07828 1.42143 8.06087 1 7 1C5.93913 1 4.92172 1.42143 4.17157 2.17157C3.42143 2.92172 3 3.93914 3 5Z"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 19V17C1 15.9391 1.42143 14.9217 2.17157 14.1716C2.92172 13.4214 3.93913 13 5 13H9"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M10 17L12 19L16 15"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ff-medium font-14">Profile</div>
              </div>
              <div
                className={`nav-item dash bg-2 d-flex align-items-center gap-2 py-2 ${
                  location.pathname === "/accounts/address" ? "active" : ""
                }`}
                onClick={() => {
                  navigate("accounts/address");
                }}
              >
                <div>
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 3V15C17 15.5304 16.7893 16.0391 16.4142 16.4142C16.0391 16.7893 15.5304 17 15 17H5C4.46957 17 3.96086 16.7893 3.58579 16.4142C3.21071 16.0391 3 15.5304 3 15V3C3 2.46957 3.21071 1.96086 3.58579 1.58579C3.96086 1.21071 4.46957 1 5 1H15C15.5304 1 16.0391 1.21071 16.4142 1.58579C16.7893 1.96086 17 2.46957 17 3Z"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7 13H13"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M8 8C8 8.53043 8.21071 9.03914 8.58579 9.41421C8.96086 9.78929 9.46957 10 10 10C10.5304 10 11.0391 9.78929 11.4142 9.41421C11.7893 9.03914 12 8.53043 12 8C12 7.46957 11.7893 6.96086 11.4142 6.58579C11.0391 6.21071 10.5304 6 10 6C9.46957 6 8.96086 6.21071 8.58579 6.58579C8.21071 6.96086 8 7.46957 8 8Z"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 5H4"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 9H4"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 13H4"
                      stroke="#000"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ff-medium font-14 text-black">Addresses</div>
              </div>
              <div
                className={`nav-item dash bg-2 d-flex align-items-center gap-2 py-2 ${
                  location.pathname === "/accounts/applicants" ? "active" : ""
                }`}
                onClick={() => {
                  navigate("accounts/applicants");
                }}
              >
                <div>
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M2.66406 4.33333C2.66406 5.21739 3.01525 6.06523 3.64037 6.69036C4.26549 7.31548 5.11334 7.66667 5.9974 7.66667C6.88145 7.66667 7.7293 7.31548 8.35442 6.69036C8.97954 6.06523 9.33073 5.21739 9.33073 4.33333C9.33073 3.44928 8.97954 2.60143 8.35442 1.97631C7.7293 1.35119 6.88145 1 5.9974 1C5.11334 1 4.26549 1.35119 3.64037 1.97631C3.01525 2.60143 2.66406 3.44928 2.66406 4.33333Z"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 16V14.3333C1 13.4493 1.35119 12.6014 1.97631 11.9763C2.60143 11.3512 3.44928 11 4.33333 11H7.66667C8.55072 11 9.39857 11.3512 10.0237 11.9763C10.6488 12.6014 11 13.4493 11 14.3333V16"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.8359 1.10742C12.553 1.29101 13.1885 1.70801 13.6423 2.29268C14.0961 2.87736 14.3425 3.59645 14.3425 4.33659C14.3425 5.07673 14.0961 5.79582 13.6423 6.3805C13.1885 6.96517 12.553 7.38217 11.8359 7.56576"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M16 16V14.3333C15.9958 13.5976 15.7483 12.884 15.2961 12.3037C14.8439 11.7233 14.2124 11.3089 13.5 11.125"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ff-medium font-14 text-black">Applicants</div>
              </div>
              <div
                className={`nav-item dash bg-2 d-flex align-items-center gap-2 py-2 ${
                  location.pathname === "/accounts/MyDocuments" ? "active" : ""
                }`}
                onClick={() => {
                  navigate("accounts/MyDocuments");
                }}
              >
                <div>
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <defs>
                      <pattern
                        id="pattern0_6617_1111"
                        patternContentUnits="objectBoundingBox"
                        width="1"
                        height="1"
                      >
                        <use
                          xlinkHref="#image0_6617_1111"
                          transform="scale(0.00416667)"
                        />
                      </pattern>
                      <image
                        id="image0_6617_1111"
                        width="240"
                        height="240"
                        preserveAspectRatio="none"
                        href="data:image/png;base64,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"
                      />
                    </defs>
                    <mask
                      id="mask0_6617_1111"
                      style={{ maskType: "alpha" }}
                      maskUnits="userSpaceOnUse"
                      x="0"
                      y="0"
                      width="17"
                      height="17"
                    >
                      <rect
                        width="17"
                        height="17"
                        fill="url(#pattern0_6617_1111)"
                      />
                    </mask>
                    <g mask="url(#mask0_6617_1111)">
                      <rect
                        x="-0.779297"
                        y="-0.566681"
                        width="18.9833"
                        height="18.7"
                        fill="#000"
                      />
                    </g>
                  </svg>
                </div>
                <div className="ff-medium font-14 text-black">Documents</div>
              </div>
            </div>
          </div>
          <div className="nav-section mb-0 mb-md-3">
            <div className="p-3 layout-border rounded">
              <p className="font-14 text-muted nav-mob-heading ">My Orders</p>
              <div
                className={`nav-item dash bg-2 d-flex align-items-center gap-2 py-2 ${
                  location.pathname === "/accounts/orders" ? "active" : ""
                }`}
                onClick={() => {
                  navigate("accounts/orders");
                }}
              >
                <div>
                  <svg
                    width="17"
                    height="17"
                    viewBox="0 0 17 17"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M6 6.83398C6 7.49703 6.26339 8.13291 6.73223 8.60175C7.20107 9.07059 7.83696 9.33398 8.5 9.33398C9.16304 9.33398 9.79893 9.07059 10.2678 8.60175C10.7366 8.13291 11 7.49703 11 6.83398C11 6.17094 10.7366 5.53506 10.2678 5.06622C9.79893 4.59738 9.16304 4.33398 8.5 4.33398C7.83696 4.33398 7.20107 4.59738 6.73223 5.06622C6.26339 5.53506 6 6.17094 6 6.83398Z"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M3.5 16.0007V15.1673C3.5 14.2833 3.85119 13.4354 4.47631 12.8103C5.10143 12.1852 5.94928 11.834 6.83333 11.834H10.1667C11.0507 11.834 11.8986 12.1852 12.5237 12.8103C13.1488 13.4354 13.5 14.2833 13.5 15.1673V16.0007"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 2.66667C1 2.22464 1.17559 1.80072 1.48816 1.48816C1.80072 1.17559 2.22464 1 2.66667 1H14.3333C14.7754 1 15.1993 1.17559 15.5118 1.48816C15.8244 1.80072 16 2.22464 16 2.66667V14.3333C16 14.7754 15.8244 15.1993 15.5118 15.5118C15.1993 15.8244 14.7754 16 14.3333 16H2.66667C2.22464 16 1.80072 15.8244 1.48816 15.5118C1.17559 15.1993 1 14.7754 1 14.3333V2.66667Z"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ff-medium font-14">My Orders</div>
              </div>
            </div>
          </div>
          <div className="nav-section mb-0 mb-md-3">
            <div className="p-3 layout-border rounded">
              <p className="font-14 text-muted nav-mob-heading ">Support</p>
              <div
                className={`nav-item dash bg-2 d-flex align-items-center gap-2 py-2 ${
                  location.pathname === "/accounts/faqs" ? "active" : ""
                }`}
                onClick={() => {
                  navigate("accounts/faqs");
                }}
              >
                <div>
                  <svg
                    width="17"
                    height="15"
                    viewBox="0 0 17 15"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M7.17969 8.94152C7.17969 9.87758 7.55154 10.7753 8.21343 11.4372C8.87532 12.0991 9.77304 12.4709 10.7091 12.4709C11.6452 12.4709 12.5429 12.0991 13.2048 11.4372C13.8667 10.7753 14.2385 9.87758 14.2385 8.94152C14.2385 8.00546 13.8667 7.10774 13.2048 6.44585C12.5429 5.78396 11.6452 5.41211 10.7091 5.41211C9.77304 5.41211 8.87532 5.78396 8.21343 6.44585C7.55154 7.10774 7.17969 8.00546 7.17969 8.94152Z"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.7969 12.0293L16.0028 14.2352"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 1H15.1176"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 6.29492H4.52941"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 11.5879H4.52941"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ff-medium font-14 text-black">FAQ's</div>
              </div>

              <div
                className="nav-item dash bg-2 d-flex align-items-center gap-2 py-2"
                onClick={handleOpenSupportModal}
              >
                <div>
                  <svg
                    width="17"
                    height="16"
                    viewBox="0 0 17 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M8.10526 12.289H3.36842C2.74028 12.289 2.13786 12.0394 1.69369 11.5953C1.24953 11.1511 1 10.5487 1 9.92054V3.60475C1 2.9766 1.24953 2.37419 1.69369 1.93002C2.13786 1.48586 2.74028 1.23633 3.36842 1.23633H12.8421C13.4703 1.23633 14.0727 1.48586 14.5168 1.93002C14.961 2.37419 15.2105 2.9766 15.2105 3.60475V7.15738"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M1 5.18359H15.2105"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M11.2656 12.2891H16.0025"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M13.6328 9.91992L16.0012 12.2883L13.6328 14.6568"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M4.16406 9.13086H4.16801"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    <path
                      d="M7.3125 9.13086H8.89145"
                      stroke="#282828"
                      strokeWidth="1.3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
                <div className="ff-medium font-14 text-black">24/7 support</div>
              </div>
            </div>
          </div>
        </div>

        <div className="sidebar-footer mt-0 mt-md-3">
          <button
            className="btn logout-btn w-100"
            onClick={() => {
              logoutFun(dispatch, navigate);
            }}
          >
            <i className="bi bi-box-arrow-right me-2"></i> Logout
          </button>
        </div>
      </div>
      {supportData && (
        <Modal
          show={showSupportModal}
          onHide={handleCloseSupportModal}
          centered
          backdrop="static"
        >
          <Modal.Header closeButton></Modal.Header>
          <Modal.Body className="rounded p-0">
            <p className="font-18 text-center ff-bold text-black py-3 border-bottom">
              Support 24/7
            </p>
            <div className="p-3 layout-border rounded-3 m-3">
              <div className="d-flex align-items-center gap-3 border-bottom pb-3">
                <div>
                  <img
                    src="https://goodhand.b-cdn.net/Assets/Upload/phone-24.svg"
                    alt=""
                  />
                </div>
                <div className="font-14 ff-semibold text-black">
                  {supportData.call_numbers}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 border-bottom py-3">
                <div>
                  <img
                    src="https://goodhand.b-cdn.net/Assets/Upload/whatsp-24.svg"
                    alt=""
                  />
                </div>
                <div className="font-14 ff-semibold text-black">
                  {supportData.whatsapp_numbers}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 border-bottom py-3 d-none">
                <div>
                  <img
                    src="https://goodhand.b-cdn.net/Assets/Upload/envlp-24.svg"
                    alt=""
                  />
                </div>
                <div className="font-14 ff-semibold text-black">
                  Connect with a representative
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 border-bottom py-3">
                <div>
                  <img
                    src="https://goodhand.b-cdn.net/Assets/Upload/at-24'.svg"
                    alt=""
                  />
                </div>
                <div className="font-14 ff-semibold text-black">
                  {supportData.email}
                </div>
              </div>
              <div className="d-flex align-items-center gap-3 border-bottom py-3 d-none">
                <div>
                  <img
                    src="https://goodhand.b-cdn.net/Assets/Upload/facebo-24.svg"
                    alt=""
                  />
                </div>
                <div className="font-14 ff-semibold text-black">Facebook</div>
              </div>
              <div className="d-flex align-items-center gap-3  pt-3 d-none">
                <div>
                  <img
                    src="https://goodhand.b-cdn.net/Assets/Upload/camers-24.svg"
                    alt=""
                  />
                </div>
                <div className="font-14 ff-semibold text-black">Instagram</div>
              </div>
            </div>
          </Modal.Body>
        </Modal>
      )}
    </div>
  );
};

export default AccountNavbar;
